/**
 * The countries a phone number can be placed in (piece 2, decision 0340): the
 * name a person picks, the ISO 3166-1 code the record keeps, the dial code
 * E.164 opens with, and the length of a national number there. 🔨 A country
 * outside this table is typed in full after the plus, under the Other choice;
 * the table is seeded, not fetched, so it grows by a row here and nowhere else.
 * The lengths are the plan's usual range for that country, not a per-area
 * numbering plan, so the check refuses a number that cannot be right and
 * accepts one that might be.
 */
export interface PhoneCountry {
  /** ISO 3166-1 alpha-2. */
  code: string;
  name: string;
  /** The digits E.164 opens with, without the plus. */
  dialCode: string;
  /** The national number's length there, shortest and longest. */
  nationalLength: readonly [number, number];
}

export const DEFAULT_PHONE_COUNTRY = 'US';

/** The two countries whose numbers share +1 and the (XXX) XXX-XXXX shape. */
export const NANP_COUNTRIES: readonly string[] = ['US', 'CA'];

export const PHONE_COUNTRIES: readonly PhoneCountry[] = [
  { code: 'US', name: 'United States', dialCode: '1', nationalLength: [10, 10] },
  { code: 'CA', name: 'Canada', dialCode: '1', nationalLength: [10, 10] },
  { code: 'MX', name: 'Mexico', dialCode: '52', nationalLength: [10, 10] },
  { code: 'GB', name: 'United Kingdom', dialCode: '44', nationalLength: [9, 10] },
  { code: 'IE', name: 'Ireland', dialCode: '353', nationalLength: [7, 9] },
  { code: 'DE', name: 'Germany', dialCode: '49', nationalLength: [6, 12] },
  { code: 'FR', name: 'France', dialCode: '33', nationalLength: [9, 9] },
  { code: 'ES', name: 'Spain', dialCode: '34', nationalLength: [9, 9] },
  { code: 'IT', name: 'Italy', dialCode: '39', nationalLength: [6, 11] },
  { code: 'NL', name: 'Netherlands', dialCode: '31', nationalLength: [9, 9] },
  { code: 'BE', name: 'Belgium', dialCode: '32', nationalLength: [8, 9] },
  { code: 'CH', name: 'Switzerland', dialCode: '41', nationalLength: [9, 9] },
  { code: 'AT', name: 'Austria', dialCode: '43', nationalLength: [7, 13] },
  { code: 'SE', name: 'Sweden', dialCode: '46', nationalLength: [7, 9] },
  { code: 'NO', name: 'Norway', dialCode: '47', nationalLength: [8, 8] },
  { code: 'DK', name: 'Denmark', dialCode: '45', nationalLength: [8, 8] },
  { code: 'FI', name: 'Finland', dialCode: '358', nationalLength: [5, 12] },
  { code: 'PL', name: 'Poland', dialCode: '48', nationalLength: [9, 9] },
  { code: 'PT', name: 'Portugal', dialCode: '351', nationalLength: [9, 9] },
  { code: 'AU', name: 'Australia', dialCode: '61', nationalLength: [9, 9] },
  { code: 'NZ', name: 'New Zealand', dialCode: '64', nationalLength: [8, 10] },
  { code: 'JP', name: 'Japan', dialCode: '81', nationalLength: [9, 10] },
  { code: 'KR', name: 'South Korea', dialCode: '82', nationalLength: [8, 10] },
  { code: 'CN', name: 'China', dialCode: '86', nationalLength: [10, 11] },
  { code: 'IN', name: 'India', dialCode: '91', nationalLength: [10, 10] },
  { code: 'SG', name: 'Singapore', dialCode: '65', nationalLength: [8, 8] },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '971', nationalLength: [8, 9] },
  { code: 'IL', name: 'Israel', dialCode: '972', nationalLength: [8, 9] },
  { code: 'ZA', name: 'South Africa', dialCode: '27', nationalLength: [9, 9] },
  { code: 'BR', name: 'Brazil', dialCode: '55', nationalLength: [10, 11] },
  { code: 'AR', name: 'Argentina', dialCode: '54', nationalLength: [10, 10] },
];

export function phoneCountry(code: string | null | undefined): PhoneCountry | null {
  if (!code) return null;
  const upper = code.toUpperCase();
  return PHONE_COUNTRIES.find((row) => row.code === upper) ?? null;
}
